import { useEffect, useMemo, useState } from 'react';
import { Colors, TextCss } from 'styles';
import { Stack } from 'common/ui/Layout';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import { Table, DefaultRow } from 'common/Table';
import { transparentize } from 'color2k';
import Text from 'common/ui/Text';
import Star from '@mui/icons-material/Star';
import StarOutline from '@mui/icons-material/StarOutline';
import { usePicklistEditor } from 'domains/reporter/Template/usePicklist';
import type { FieldBuilderFormErrors } from './types';

import { DEFAULT_BLANK_PICKLIST_OPTION } from './FieldBuilderPane.Picklist';
import { slateToString } from '../../RichTextEditor/utils/slateToString';
import styled from 'styled-components';
import Tooltip from '@mui/material/Tooltip';
import type { ColumnDef } from '@tanstack/react-table';
import type { Node } from 'domains/reporter/RichTextEditor/core';
import type { ReportPicklistOption } from 'generated/graphql';
import { rootTheme } from 'theme/rootTheme';

const NUM_PLACEHOLDER_ROWS = 5;
const PLACEHOLDER_NAME = '\u00A0';

type PicklistTableRow = {
  id?: string;
  name?: string | null;
  text?: string;
  default?: boolean | null;
  isPlaceholder?: boolean;
};

const IconsGroup = styled.div`
  display: flex;
  justify-content: ${({ isBlankDefault }: { isBlankDefault: boolean }) =>
    isBlankDefault ? 'flex-end' : 'space-between'};
  padding-right: 12px;
  align-items: center;
`;

const toSlateFragment = (text: ReportPicklistOption['text']): ReadonlyArray<Node> => {
  return Array.isArray(text) ? (text as unknown as ReadonlyArray<Node>) : [];
};

const getPicklistPreview = (text: ReportPicklistOption['text']): string => {
  const displayText = slateToString(toSlateFragment(text)).trim().split('\n');
  if (displayText.length === 1) {
    // blank default option, don't insert extra space
    if (displayText[0] === '') return DEFAULT_BLANK_PICKLIST_OPTION.trim();
    // single line picklist option
    return displayText[0];
  } else {
    // multi-line picklist option
    return displayText.join('...');
  }
};

const Cell = ({
  isDefault,
  onEditPicklistOption,
  isBlankDefault,
  onSetAsDefault,
}: {
  isDefault: boolean;
  onEditPicklistOption: () => void;
  isBlankDefault: boolean;
  onSetAsDefault: () => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <IconsGroup
      data-testid="picklist-icon-group"
      onMouseLeave={() => setIsHovered(false)}
      isBlankDefault={isBlankDefault}
      className={`${isDefault ? 'isDefault' : 'notDefault'}`}
    >
      {!isBlankDefault && (
        <div className="picklist-action edit-icon">
          <Tooltip title="Edit">
            <EditIcon
              data-testid="edit-picklist"
              onClick={onEditPicklistOption}
              fontSize="inherit"
            />
          </Tooltip>
        </div>
      )}
      {isDefault ? (
        <Tooltip title="Default">
          <Star
            fontSize="inherit"
            className="picklist-action default-icon"
            data-testid="set-as-default"
          />
        </Tooltip>
      ) : isHovered ? (
        <Tooltip title="Set as default">
          <Star
            data-testid="set-as-default-hover"
            fontSize="inherit"
            className="picklist-action default-icon"
            onClick={onSetAsDefault}
          />
        </Tooltip>
      ) : (
        <StarOutline
          data-testid="set-as-default"
          fontSize="inherit"
          className="picklist-action set-default-icon"
          onMouseEnter={() => setIsHovered(true)}
        />
      )}
    </IconsGroup>
  );
};

export const FieldBuilderPanePicklistTable = ({
  handleRemovePicklistOption,
  handleEditPicklistOption,
  handleSetAsDefault,
  formErrors,
  editOptionId,
}: {
  handleRemovePicklistOption: (arg1: string) => void;
  handleEditPicklistOption: (id: string) => void;
  handleSetAsDefault: (id: string) => void;
  formErrors: FieldBuilderFormErrors;
  editOptionId: string;
}): React.ReactElement | null => {
  const { activePicklist } = usePicklistEditor();
  const [defaultOption, setDefaultOption] = useState(0);

  // The default flag is optional and if not set, the first option is the default. This also handles
  // when a default option is removed and the first option should be reset as default.
  useEffect(() => {
    if (activePicklist?.options) {
      const defaultOptionIndex = activePicklist.options.findIndex(
        (option) => option.default === true
      );
      setDefaultOption(defaultOptionIndex > -1 ? defaultOptionIndex : 0);
    }
  }, [activePicklist?.options]);

  const tableData = useMemo<PicklistTableRow[]>(() => {
    const normalizedOptions =
      activePicklist?.options?.map<PicklistTableRow>((option) => ({
        id: option.id,
        name: option.name,
        text: getPicklistPreview(option.text),
        default: option.default,
      })) ?? [];

    const placeholderCount = Math.max(NUM_PLACEHOLDER_ROWS - normalizedOptions.length, 0);

    const placeholders = Array.from({ length: placeholderCount }, () => ({
      name: PLACEHOLDER_NAME,
      isPlaceholder: true,
    }));

    return [...normalizedOptions, ...placeholders];
  }, [activePicklist?.options]);

  const columns = useMemo<Array<ColumnDef<PicklistTableRow>>>(
    () => [
      {
        header: PLACEHOLDER_NAME,
        id: 'edit',
        cell: ({ row: { index, original } }) => {
          if (
            activePicklist?.options.length !== undefined &&
            index > activePicklist?.options.length - 1
          ) {
            return null;
          }
          return (
            <Cell
              isBlankDefault={original.text === DEFAULT_BLANK_PICKLIST_OPTION.trim()}
              isDefault={index === defaultOption}
              onEditPicklistOption={() => original.id && handleEditPicklistOption(original.id)}
              onSetAsDefault={() => original.id && handleSetAsDefault(original.id)}
            />
          );
        },
        size: 2,
      },
      {
        header: `${PLACEHOLDER_NAME}#`,
        id: 'default',
        accessorFn: (row, index) =>
          row.name === PLACEHOLDER_NAME
            ? PLACEHOLDER_NAME
            : `${PLACEHOLDER_NAME}${PLACEHOLDER_NAME}` + (index + 1),
        size: 1,
      },
      {
        header: 'Name',
        id: 'name',
        accessorFn: (row) => row.name ?? '',
        size: 3,
      },
      {
        header: 'Value',
        id: 'value',
        cell: ({ row }) => {
          if (row.original.text === DEFAULT_BLANK_PICKLIST_OPTION.trim()) {
            return 'Blank';
          }

          const texts = row.original.text?.split('\n');
          const elements: Array<
            | React.ReactElement<React.ComponentProps<'br'>>
            | React.ReactElement<React.ComponentProps<'span'>>
          > = [];
          texts?.forEach((text, index) => {
            elements.push(<span key={`picklist-text-${index}`}>{text}</span>);
            if (index < texts.length - 1) {
              elements.push(<br />);
            }
          });
          return elements;
        },
        size: 6,
      },
      {
        header: () => null,
        id: 'remove',
        accessorKey: 'id',
        cell: ({ row }) => {
          if (
            row.original.name === PLACEHOLDER_NAME ||
            (row.index === 0 && row.original.text === DEFAULT_BLANK_PICKLIST_OPTION.trim())
          ) {
            return <div />;
          }

          return (
            <IconButton
              className="picklist-action"
              data-testid="delete-picklist-item"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (row.original.id) {
                  handleRemovePicklistOption(row.original.id);
                }
              }}
            >
              <RemoveCircleIcon
                css={`
                  ${TextCss.body1}
                  color: ${Colors.red6};
                `}
              />
            </IconButton>
          );
        },
        size: 1,
      },
    ],
    [
      activePicklist?.options.length,
      defaultOption,
      handleEditPicklistOption,
      handleRemovePicklistOption,
      handleSetAsDefault,
    ]
  );

  return (
    <Stack data-testid="picklist-table" vertical stretchX>
      <Text css={{ paddingLeft: '12px', marginTop: '12px' }} variant="caption">
        Picklist options
      </Text>
      <Table
        columns={columns}
        Row={({ row, ...rest }) => {
          return (
            <DefaultRow
              row={row}
              data-testid="picklist-option-row"
              css={`
                background-color: ${row.original.id === editOptionId
                  ? Colors.mainBlueHover
                  : 'initial'};
                border-top: 1px solid ${transparentize(Colors.gray10, 0.88)};
                div:nth-of-type(4) {
                  font-style: ${row.original.text === DEFAULT_BLANK_PICKLIST_OPTION.trim()
                    ? 'italic'
                    : 'normal'};
                }
                div:nth-of-type(5) {
                  text-align: right;
                }
                ${row.original.text &&
                `&:hover {
                  background-color: ${
                    row.original.id === editOptionId ? Colors.mainBlueHover : Colors.gray3
                  };
                  .picklist-action {
                    cursor: pointer;
                    opacity: 1;
                  }
                }`}
                .picklist-action {
                  opacity: 0;
                  &.default-icon {
                    opacity: 1;
                  }
                }
              `}
              {...rest}
            />
          );
        }}
        data={tableData}
        css={`
          background-color: ${Colors.gray1};
          margin-top: 4px;
          max-height: 232px;
          overflow-y: auto;
          border-radius: ${rootTheme.radius.sm}px;
          .table-header {
            background-color: ${Colors.gray2};
          }
          border: 1px solid ${formErrors.picklistForm.options !== '' ? Colors.red6 : 'transparent'};
        `}
      />
      {formErrors.picklistForm.options !== '' && (
        <Text
          data-testid="picklist-table-error"
          variant="caption"
          css={{
            marginTop: 4,
            marginLeft: 12,
            color: Colors.red6,
          }}
        >
          {formErrors.picklistForm.options}
        </Text>
      )}
    </Stack>
  );
};
